import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useOutletContext } from "react-router";
import axios from "axios";
import _ from "lodash";
import WhiteCard from "features/metadesk/importList/components/WhiteCard";
import BehaviorBarChart from "./BehaviorBarChart";
import BehaviorCircleChart from "./BehaviorCircleChart";
import ProgressBar from "@metacrm/metacrm-material-ui/dist/ProgressBar";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useFetchUserList } from "features/metadesk/redux/fetchUserList";
import {
  formatWalletAgeDisplay,
  getTokenImageSrc,
  validateImage,
} from "features/helpers/utils";
import { apiUrl, cexList } from "features/configure";
import { useSelector } from "react-redux";
import {
  selectEntityChainList,
  selectEnabledEcosystems,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import BehaviorRowBarChart from "./BehaviorRowBarChart";
import BehaviorTable from "./BehaviotTable";
import { useIntl } from "react-intl";
import { groupBy } from "lodash";
import useGetWalletValueDistributionTable from "../hooks/useGetWalletValueDistributionTable";

const initialState = {
  data: {
    userCount: 0,
    walletCount: 0,
    walletTotalValue: 0,
    averageWalletAge: 0,
    averageWalletValue: 0,
    walletValues: [],
    walletValueDistribution: [],
    walletAges: [],
    tokenHoldings: [],
    nftHoldings: [],
    chainPortfolio: [],
    whales: [],
    protocolList: [],
    txns: [],
    cex: [],
  },
  status: "Loading",
  progressId: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_DATA":
      return {
        ...state,
        data: { ...state.data, ...action.payload },
        status: "Complete",
      };
    case "SET_LOADING":
      return { ...state, status: "Loading", progressId: new Date().getTime() };
    case "SET_RESULT":
      return { ...state, status: "Result" };
    case "SET_ERROR":
      return { ...state, status: "Complete" };
    default:
      return state;
  }
}

const processPercentageData = (data, userCount) =>
  _.map(data, (item) => {
    const value = (100 * (parseInt(item.value) / (userCount || 1))).toFixed(1);
    return {
      ...item,
      value: value >= 100 ? 100 : value,
    };
  });

const prepareFilter = (filter) => ({
  ...filter,
  source: filter?.source?.value,
  lastEngagement: filter?.lastEngagement?.every((item) => item !== null)
    ? { from: filter?.lastEngagement[0], to: filter?.lastEngagement[1] }
    : null,
  lastEngagementType: filter?.lastEngagementType?.value,
  cexLabels: filter?.cexLabels?.map((label) =>
    typeof label === "object" ? label.value : label
  ),
  tags: filter?.tags?.map((tag) => (typeof tag === "object" ? tag._id : tag)),
  ecosystems: filter?.ecosystems?.map((ecosystem) =>
    typeof ecosystem === "object" ? ecosystem.value : ecosystem
  ),
  chainIds: filter?.chainIds?.map((chainId) =>
    typeof chainId === "object" ? chainId.value : chainId
  ),
  assets: filter?.assets
    ?.filter((item) => item.chainId)
    .map((asset) => ({
      chainId: asset.chainId.value,
      assetType: asset.assetType.value,
      token: { address: asset.token.value },
      included: true,
    })),
  walletTotalValue: filter?.walletTotalValue && {
    from: filter?.walletTotalValue[0],
    to: filter?.walletTotalValue[1],
  },
  walletAge: filter?.walletAge && {
    from: formatWalletAgeDisplay(filter?.walletAge[0]),
    to: formatWalletAgeDisplay(filter?.walletAge[1]),
  },
  userIds: filter?.selectedUser,
});

function CustomerReport({ hasFilter }) {
  const { formatMessage } = useIntl();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [entityName] = useOutletContext();
  const { userListFilter: filter } = useFetchUserList();
  const dispatchSnackbar = useDispatchSnackbar();
  const enabledEcosystems = useSelector(selectEnabledEcosystems);
  const chainList = useSelector(selectEntityChainList);

  const onlySolanaEnabled =
    enabledEcosystems.length === 1 && enabledEcosystems[0] === "solana";

  const { headers: walletValueDistributionHeaders } =
    useGetWalletValueDistributionTable();
  const [popMenuFilter, setPopMenuFilter] = React.useState({
    tokenChainFilter: {
      label: formatMessage({ id: "global.filterCriteria.allChains" }),
      value: "",
    },
    tokenTypeFilter: {
      label: formatMessage({ id: "global.filterCriteria.allTypes" }),
      value: "",
    },
    nftChainFilter: {
      label: formatMessage({ id: "global.filterCriteria.allChains" }),
      value: "",
    },
    protocolFilter: {
      label: formatMessage({ id: "global.filterCriteria.allChains" }),
      value: "",
    },
    txnsChainFilter: {
      label: formatMessage({ id: "global.filterCriteria.allChains" }),
      value: "",
    },
  });
  const [searchText, setSearchText] = useState({
    token: "",
    nft: "",
  });

  useEffect(() => {
    if (entityName === "manta") {
      setPopMenuFilter({
        tokenChainFilter: { label: "Manta", value: "169" },
        tokenTypeFilter: {
          label: formatMessage({ id: "global.filterCriteria.allTypes" }),
          value: "",
        },
        nftChainFilter: { label: "Manta", value: "169" },
        protocolFilter: { label: "Manta", value: "169" },
        txnsChainFilter: { label: "Manta", value: "169" },
      });
    }
  }, [entityName]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function fetchReport() {
      dispatch({ type: "SET_LOADING" });
      const postFilter = hasFilter ? prepareFilter(filter) : "";
      try {
        const { data } = await axios.post(
          `${apiUrl}/api/customerReport/${entityName}`,
          { filter: postFilter, userIds: filter?.selectedUser },
          { signal }
        );
        dispatch({ type: "SET_DATA", payload: data });
      } catch (err) {
        if (err.code !== "ERR_CANCELED") {
          dispatch({ type: "SET_ERROR" });
          dispatchSnackbar({ err });
        }
      }
    }
    fetchReport();

    return () => {
      controller.abort();
    };
  }, [filter]);

  const convertTokenTypeLabel = (tokenType) => {
    let name;

    switch (tokenType) {
      case "all":
        name = formatMessage({ id: "global.demographic.netWorth" });
        break;
      case "stable":
        name = formatMessage({ id: "global.demographic.stablecoins" });
        break;
      case "native":
        name = formatMessage({ id: "global.demographic.nativeTokens" });
        break;
      case "alt":
        name = formatMessage({ id: "global.demographic.altcoins" });
        break;
      default:
        name = tokenType.charAt(0).toUpperCase() + tokenType.slice(1);
    }

    return name;
  };

  const processValueData = (data) => {
    const formatter = (value, decimal = 0) =>
      value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
      });

    return _.map(Object.entries(data), ([key, value]) => {
      const tokenType = convertTokenTypeLabel(key);

      return {
        tokenType,
        total: formatter(value.total),
        q1: formatter(value.q1),
        q2: formatter(value.q2),
        q3: formatter(value.q3),
        q4: formatter(value.q4),
      };
    });
  };

  const { data, status, progressId } = state;

  const {
    userCount,
    walletCount,
    averageWalletAge,
    averageWalletValue,
    walletAges,
    walletValues,
    walletValueDistribution,
    tokenHoldings,
    nftHoldings,
    chainPortfolio,
    protocolList,
    txns,
    cex,
  } = data;

  const walletAgesData = useMemo(
    () => processPercentageData(walletAges, userCount),
    [walletAges, userCount]
  );
  const walletValueDistributionData = useMemo(
    () => processValueData(walletValueDistribution),
    [walletValueDistribution]
  );

  const getChainImg = (item) => {
    return (
      chainList.find((chain) => {
        return chain?.id?.toString() === item?.id?.toString();
      })?.logo_url || require("assets/img/empty-token.svg").default
    );
  };

  const [validatedTokenHoldings, setValidatedTokenHoldings] = useState([]);

  useEffect(() => {
    async function validateTokenImages() {
      // 驗證前 10 個圖片

      const filteredHoldings = tokenHoldings.filter(
        (item) =>
          (!popMenuFilter?.tokenChainFilter?.value ||
            item.id == popMenuFilter?.tokenChainFilter?.value) &&
          (!popMenuFilter?.tokenTypeFilter?.value ||
            item.tokenType === popMenuFilter?.tokenTypeFilter?.value) &&
          (!searchText.token ||
            _.includes(_.toLower(item?.name), _.toLower(searchText.token)))
      );

      const verifiedHoldings = await Promise.all(
        filteredHoldings?.slice(0, 10)?.map(async (item) => {
          const validIcon = await validateImage(
            getTokenImageSrc({ ...item, chainId: item.id }),
            "token"
          );
          return {
            ...item,
            name: `${item.name}--${item.id}`,
            icon: validIcon,
            chain: getChainImg(item),
          };
        })
      );

      setValidatedTokenHoldings(verifiedHoldings);
    }

    validateTokenImages();
  }, [
    tokenHoldings,
    popMenuFilter?.tokenChainFilter,
    popMenuFilter?.tokenTypeFilter,
    searchText?.token,
  ]);

  const nftHoldingsData = useMemo(() => {
    const processNftHoldings = (holdings) => {
      return holdings?.slice(0, 10)?.map((item) => ({
        ...item,
        icon: require("assets/img/ic_nft.svg").default,
        chain: getChainImg(item),
      }));
    };

    const filteredHoldings = nftHoldings.filter(
      (item) =>
        (!popMenuFilter?.nftChainFilter?.value ||
          item.id == popMenuFilter?.nftChainFilter?.value) &&
        (!searchText.nft ||
          _.includes(_.toLower(item?.name), _.toLower(searchText.nft)))
    );

    return processNftHoldings(filteredHoldings);
  }, [nftHoldings, popMenuFilter, chainList, searchText]);

  const chainPortfolioData = useMemo(() => {
    const processedData = processPercentageData(chainPortfolio, userCount);

    // 获取前 9 名
    const top9 = processedData?.slice(0, 9)?.map((item) => ({
      ...item,
      icon: getChainImg(item),
    }));

    // 计算 "Others" 的值
    const othersValue = processedData
      .slice(9)
      .reduce((acc, item) => acc + parseFloat(item.value), 0);

    if (othersValue > 0) {
      top9.push({
        id: "others",
        name: "Others",
        value: othersValue.toFixed(1),
        icon: require("assets/img/other.png"),
      });
    }

    return top9;
  }, [chainPortfolio, userCount]);

  const otherChainData = useMemo(() => {
    const processedData = processPercentageData(chainPortfolio, userCount);
    return processedData.slice(9);
  }, [chainPortfolio, userCount]);

  // Add protocolData only when entityName is "manta"
  const protocolData = useMemo(() => {
    const processProtocolList = (holdings) => {
      return holdings?.slice(0, 10)?.map((item) => ({
        ...item,
        icon: item?.image || require("assets/img/empty-protocol.svg").default,
        chain: getChainImg(item),
      }));
    };

    const filteredHoldings = popMenuFilter?.protocolFilter?.value
      ? protocolList.filter(
          (item) => item.id === parseInt(popMenuFilter?.protocolFilter?.value)
        )
      : protocolList;

    return processProtocolList(filteredHoldings);
  }, [protocolList, popMenuFilter, chainList]);

  const walletTxnsDistributionData = useMemo(() => {
    const processWalletTxnsDistributionData = (data, type = "percent") => {
      return _.map(data, (item) => ({
        name: item.name,
        value:
          type === "percent"
            ? Number((item.value / userCount) * 100).toFixed(1)
            : item.value,
      }));
    };

    const filteredWalletTxns = popMenuFilter?.txnsChainFilter?.value
      ? txns[popMenuFilter.txnsChainFilter.value]
      : txns["all"] || [];

    return processWalletTxnsDistributionData(filteredWalletTxns);
  }, [txns, popMenuFilter, chainList]);

  const cexDistributionData = useMemo(() => {
    const defaultCexIcon = require("assets/img/ic_cex.svg").default;

    const processCexDistributionData = (cexData) => {
      // filtered out Unknow CEX
      const filteredCexData = cexData
        .filter((item) => item.id !== "Unknown")
        .slice(0, 10);

      return _.map(filteredCexData, (item) => {
        const cexInfo = cexList.find((cex) => cex.id === item.id);

        return {
          name: item.id,
          value: item.value,
          icon: cexInfo ? cexInfo.icon : defaultCexIcon,
        };
      });
    };

    return processCexDistributionData(cex);
  }, [cex]);

  const getChainFilterList = (data, anotherMethod = false) => {
    const isManta = entityName === "manta";
    // Cuz txns data has different structure, so we need to handle it differently
    const chainIds = anotherMethod
      ? Object.keys(data).filter((key) => key !== "all")
      : Object.keys(groupBy(data, "id"));
    const hasManta = _.includes(chainIds, "169");

    return [
      {
        label: formatMessage({ id: "global.filterCriteria.allChains" }),
        value: "",
      },
      ...(isManta && !hasManta ? [{ label: "Manta", value: "169" }] : []),
      ...chainIds
        .map((key) => {
          const name = chainList.find(
            (item) => item.id === parseInt(key)
          )?.name;
          return {
            label: name,
            value: key,
          };
        })
        .filter((item) => item?.label && item?.value),
    ];
  };

  const tokenHoldingsChainOptions = useMemo(() => {
    return getChainFilterList(tokenHoldings);
  }, [tokenHoldings]);
  const nftHoldingsChainOptions = useMemo(() => {
    return getChainFilterList(nftHoldings);
  }, [nftHoldings]);
  const protocolChainOptions = useMemo(() => {
    return getChainFilterList(protocolList);
  }, [protocolList]);
  const walletTxnsDistributionChainOptions = useMemo(() => {
    return getChainFilterList(txns, true);
  }, [txns]);

  const tokenHoldingsTypeOptions = useMemo(() => {
    const uniqueTypes = _.uniq(
      tokenHoldings
        .filter((item) => item.tokenType) // avoid undefined tokenType
        .map((item) => item.tokenType)
    );

    return [
      {
        label: formatMessage({ id: "global.filterCriteria.allTypes" }),
        value: "",
      },
      ...uniqueTypes.map((type) => ({
        label: convertTokenTypeLabel(type),
        value: type,
      })),
    ];
  }, [tokenHoldings]);

  if (status !== "Result") {
    return (
      <Box
        sx={{
          height: `calc(100vh - ${hasFilter ? "220px" : "165px"})`,
          width: "100%",
          bgcolor: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box fontSize={16} color="#000" mb="8px">
          {formatMessage({ id: "customerAnalysis.chart.loading.title" })}
        </Box>
        <Box color="#555" fontWeight={400} mb="32px">
          {formatMessage({
            id: "customerAnalysis.chart.walletValueDistribution",
          })}
        </Box>
        <Box sx={{ width: "360px" }}>
          <ProgressBar
            key={progressId}
            isComplete={status === "Complete"}
            onComplete={() => dispatch({ type: "SET_RESULT" })}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ gap: "10px", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", gap: "10px" }}
      >
        <WhiteCard
          label={
            <Box>
              {formatMessage({ id: "global.count.users" })}{" "}
              <IconTooltip
                content={formatMessage({
                  id: "customerAnalysis.matrix.tooltip.userCount",
                })}
              />
            </Box>
          }
          value={userCount}
          width="100%"
        />
        <WhiteCard
          label={
            <Box>
              {formatMessage({ id: "global.count.totalWallet" })}{" "}
              <IconTooltip
                content={formatMessage({
                  id: "customerAnalysis.matrix.tooltip.totalWalletCount",
                })}
              />
            </Box>
          }
          value={walletCount}
          width="100%"
        />
      </Box>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: "stretch",
          height: "300px",
        }}
      >
        {/* Wallet Age Distribution */}
        {!onlySolanaEnabled && (
          <Grid item sm={4} sx={{ height: "100%" }}>
            <BehaviorCircleChart
              data={walletAgesData}
              chartName={formatMessage({
                id: "customerAnalysis.chart.walletAgeDistribution",
              })}
              tooltip={formatMessage({
                id: "customerAnalysis.chart.tooltip.walletAgeDistribution",
              })}
              average={formatMessage(
                { id: "global.chart.tag.year" },
                { number: averageWalletAge }
              )}
            />
          </Grid>
        )}
        <Grid item sm={!onlySolanaEnabled ? 8 : 12} sx={{ height: "100%" }}>
          {/* Wallet Value Distribution */}
          <BehaviorTable
            tableName={formatMessage({
              id: "customerAnalysis.chart.walletTotalValueDistribution",
            })}
            headers={walletValueDistributionHeaders}
            data={walletValueDistributionData}
            tooltip={formatMessage({
              id: "customerAnalysis.chart.tooltip.walletTotalValueDistribution",
            })}
          />
        </Grid>
      </Grid>
      {/* Chain Portfolio */}
      <BehaviorBarChart
        data={chainPortfolioData}
        chartName={formatMessage({ id: "global.demographic.chainUsage" })}
        otherDatas={otherChainData}
        type="percent"
        tooltip={formatMessage({
          id: "customerAnalysis.chart.tooltip.chainUsage",
        })}
      />
      {/* Top Token Holdings */}
      <BehaviorRowBarChart
        type="topTokenHoldings"
        data={validatedTokenHoldings}
        filters={[
          {
            label: "Token Type",
            options: tokenHoldingsTypeOptions,
            selectedOption: popMenuFilter?.tokenTypeFilter,
            setOption: (v) =>
              setPopMenuFilter({ ...popMenuFilter, tokenTypeFilter: v }),
          },
          {
            label: "Chain",
            options: tokenHoldingsChainOptions,
            selectedOption: popMenuFilter?.tokenChainFilter,
            setOption: (v) =>
              setPopMenuFilter({ ...popMenuFilter, tokenChainFilter: v }),
          },
        ]}
        chartName={formatMessage({
          id: "global.demographic.topTokenHoldings",
        })}
        otherDatas={{ userCount: userCount }}
        showSearchBar
        searchText={searchText.token}
        setSearchText={(v) => setSearchText({ ...searchText, token: v })}
        tooltip={formatMessage({
          id: "customerAnalysis.chart.tooltip.topTokenHoldings",
        })}
      />
      {/* Top NFT Holdings */}
      <BehaviorBarChart
        data={nftHoldingsData}
        chartName={formatMessage({ id: "global.demographic.nftHoldings" })}
        filters={[
          {
            label: "Chain",
            options: nftHoldingsChainOptions,
            selectedOption: popMenuFilter.nftChainFilter,
            setOption: (v) =>
              setPopMenuFilter({ ...popMenuFilter, nftChainFilter: v }),
          },
        ]}
        type="number"
        showSearchBar
        searchText={searchText.nft}
        setSearchText={(v) => setSearchText({ ...searchText, nft: v })}
        tooltip={formatMessage({
          id: "customerAnalysis.chart.tooltip.topNftHoldings",
        })}
      />
      {/* Top Protocols */}
      {(entityName === "manta" || enabledEcosystems.includes("solana")) && (
        <BehaviorRowBarChart
          type="topPortocols"
          data={protocolData}
          filters={[
            {
              label: "Chain",
              options: protocolChainOptions,
              selectedOption: popMenuFilter?.protocolFilter,
              setOption: (v) =>
                setPopMenuFilter({ ...popMenuFilter, protocolFilter: v }),
            },
          ]}
          otherDatas={{ userCount: userCount }}
          chartName={formatMessage({ id: "global.demographic.protocol" })}
        />
      )}
      {/* Wallet Transactions Count Distribution */}
      <BehaviorBarChart
        data={walletTxnsDistributionData}
        chartName={formatMessage({
          id: "global.demographic.userTransactionCount",
        })}
        filters={[
          {
            label: "Chain",
            options: walletTxnsDistributionChainOptions,
            selectedOption: popMenuFilter.txnsChainFilter,
            setOption: (v) =>
              setPopMenuFilter({ ...popMenuFilter, txnsChainFilter: v }),
          },
        ]}
        tooltip={formatMessage({
          id: "customerAnalysis.chart.tooltip.userTransactionCount",
        })}
      />
      {/* CEX Label Distribution */}
      <BehaviorBarChart
        data={cexDistributionData}
        chartName={formatMessage({ id: "global.demographic.cexPlatformUsage" })}
        type="number"
        tooltip={formatMessage({
          id: "customerAnalysis.chart.tooltip.cexPlatformUsage",
        })}
      />
    </Box>
  );
}

export default CustomerReport;
